import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Avatar,
  Button,
  TextField,
  Switch,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  Chip,
  IconButton,
  Snackbar,
  Stack,
  Menu,
  MenuItem,
  alpha,
  useTheme,
  Collapse,
  LinearProgress,
  Divider,
  Slider, // Added
  Tooltip, // Added
} from '@mui/material';
import {
  Edit as EditIcon,
  PhotoCamera as PhotoCameraIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  Person as PersonIcon,
  MonitorHeart as MetricsIcon,
  Speed as ZonesIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Sync as SyncIcon,
  Email as EmailIcon,
  Palette as PaletteIcon,
  Visibility as VisibilityIcon,
  Lock as LockIcon,
  DirectionsRun as DirectionsRunIcon,
  FitnessCenter as FitnessCenterIcon,
  Groups as GroupsIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon, // Added
  Error as ErrorIcon, // Added
  Pending as PendingIcon, // Added
  History as HistoryIcon, // Added
} from '@mui/icons-material';
import {
  useGetCurrentUserQuery,
  useUpdateUserProfileMutation,
  useUpdateNotificationPreferencesMutation,
  useRequestRoleChangeMutation,
  useDeleteAccountMutation,
  useEstimateVO2maxQuery,
  useEstimateLTHRQuery,
  useGetHeartRateZonesQuery,
  useGetPaceZonesQuery,
  useCalculateHeartRateZonesMutation,
  useCalculatePaceZonesMutation,
  useSetManualHeartRateZonesMutation,
  useSetManualPaceZonesMutation,
  baseApi, // Added baseApi
} from '../../../store/api/baseApi';
import apiClient from '../../../utils/apiClient'; // Added apiClient
import { format, formatDistanceToNow } from 'date-fns'; // Added date-fns
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { useSearchParams } from 'react-router-dom';
import { setTheme } from '../../../store/slices/uiSlice';
import { type UserRole } from '../../../store/api/models/userModels';
import ZonesTable from '../../analytics/components/ZonesTable';
import { AutoFixHigh as AutoFixIcon } from '@mui/icons-material';

/**
 * UserSettingsPage Component
 * 
 * Provides comprehensive user settings management including:
 * - Profile information (name, email, profile picture)
 * - Theme preferences (light/dark mode selection)
 * - Role management (current role display, request role change)
 * - Strava connection management (status, reconnect/disconnect)
 * - Notification preferences (email, push, workout reminders, trainer updates)
 * - Privacy settings
 * - Account deletion with confirmation
 * - Sync History Integration
 * - Offline support with queued changes indicator
 */

// Interfaces for Sync History
interface SyncTaskMetadata {
  sync_type?: string;
  force_full?: boolean;
  after_days?: number;
  max_pages?: number;
  source?: string;
  started_at?: string;
  end_time?: string;
  duration_seconds?: number;
  activities_created?: number;
  activities_updated?: number;
  streams_synced?: number;
  errors?: string[];
  status?: string;
}

interface SyncHistoryItem {
  id: number;
  user_id: number;
  task_id: string;
  status: 'pending' | 'active' | 'completed' | 'failed' | 'cancelled';
  source: string;
  started_at: string;
  completed_at: string | null;
  error_message: string | null;
  activities_synced: number;
  metadata_info: SyncTaskMetadata;
}

interface SyncHistoryResponse {
  tasks: SyncHistoryItem[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

const UserSettingsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const [searchParams] = useSearchParams();

  // Fetch user data from API
  const { data: userData, error, isLoading, refetch } = useGetCurrentUserQuery();
  const [updateUserProfile] = useUpdateUserProfileMutation();
  const [updateNotificationPreferences] = useUpdateNotificationPreferencesMutation();
  const [requestRoleChange] = useRequestRoleChangeMutation();
  const [deleteAccount] = useDeleteAccountMutation();

  // Get current theme from Redux store
  const currentTheme = useAppSelector((state) => state.ui.theme || 'light');

  // Profile state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [profilePicture, setProfilePicture] = useState('');
  const [originalProfileData, setOriginalProfileData] = useState({
    fullName: '',
    email: '',
    profile_picture_url: '',
  });

  // Role state
  const [currentRole, setCurrentRole] = useState<'athlete' | 'trainer' | 'both' | 'superuser'>('athlete');
  const [requestedRole, setRequestedRole] = useState<'athlete' | 'trainer' | 'both' | null>(null);
  const [roleChangeReason, setRoleChangeReason] = useState('');
  const [showRoleChangeDialog, setShowRoleChangeDialog] = useState(false);

  // Strava connection state
  const [isStravaConnected, setIsStravaConnected] = useState(false);
  const [stravaConnectionDate, setStravaConnectionDate] = useState('');

  // Notification preferences state
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [syncNotifications, setSyncNotifications] = useState(true);
  const [weeklySummary, setWeeklySummary] = useState(true);

  // Privacy settings state
  const [profileVisibility, setProfileVisibility] = useState<'public' | 'private'>('public');
  const [activityVisibility, setActivityVisibility] = useState<'followers' | 'public' | 'private'>('followers');

  const [isEditingMetrics, setIsEditingMetrics] = useState(false);
  const [maxHr, setMaxHr] = useState<number | ''>('');
  const [weight, setWeight] = useState<number | ''>('');
  const [restingHr, setRestingHr] = useState<number | ''>('');
  const [age, setAge] = useState<number | ''>('');
  const [vo2max, setVo2max] = useState<number | ''>('');
  const [originalMetricsData, setOriginalMetricsData] = useState<{
    maxHr: number | '';
    weight: number | '';
    restingHr: number | '';
    age: number | '';
    vo2max: number | '';
  }>({
    maxHr: '',
    weight: '',
    restingHr: '',
    age: '',
    vo2max: ''
  });

  // VO2max Estimation Dialog state
  const [showVO2maxEstimateDialog, setShowVO2maxEstimateDialog] = useState(false);
  const [shouldFetchEstimate, setShouldFetchEstimate] = useState(false);

  // LTHR Estimation Dialog state
  const [showLTHREstimateDialog, setShowLTHREstimateDialog] = useState(false);
  const [shouldFetchLTHREstimate, setShouldFetchLTHREstimate] = useState(false);

  // VO2max & LTHR Estimation Logic
  const { data: estimateData, isFetching: isEstimating, isError: isEstimateError } = useEstimateVO2maxQuery(
    {
      max_hr: Number(maxHr) || 0,
      age: Number(age) || 0,
      resting_hr: Number(restingHr) || 60
    },
    { skip: !showVO2maxEstimateDialog || !shouldFetchEstimate || !maxHr || !age }
  );

  const { data: lthrEstimateData, isFetching: isEstimatingLTHR, isError: isLTHREstimateError } = useEstimateLTHRQuery(
    { max_hr: Number(maxHr) || 0 },
    { skip: (!showVO2maxEstimateDialog && !showLTHREstimateDialog) || (!shouldFetchEstimate && !shouldFetchLTHREstimate) || !maxHr }
  );

  // NEW: Performance Zones State (FTP, Pace)
  const [isEditingZones, setIsEditingZones] = useState(false);
  const [ftpCycling, setFtpCycling] = useState<number | ''>('');
  const [thresholdPaceRunning, setThresholdPaceRunning] = useState<number | ''>('');
  const [lactateThresholdHeartRate, setLactateThresholdHeartRate] = useState<number | ''>('');
  const [originalZonesData, setOriginalZonesData] = useState<{ ftpCycling: number | ''; thresholdPaceRunning: number | ''; lactateThresholdHeartRate: number | '' }>({
    ftpCycling: '',
    thresholdPaceRunning: '',
    lactateThresholdHeartRate: ''
  });

  // NEW: Zone Management Hooks
  const { data: hrZones, refetch: refetchHRZones } = useGetHeartRateZonesQuery();
  const { data: paceZones, refetch: refetchPaceZones } = useGetPaceZonesQuery();
  const [calculateHRZones, { isLoading: isCalculatingHR }] = useCalculateHeartRateZonesMutation();
  const [calculatePaceZones, { isLoading: isCalculatingPace }] = useCalculatePaceZonesMutation();
  const [saveHRZones] = useSetManualHeartRateZonesMutation();
  const [savePaceZones] = useSetManualPaceZonesMutation();

  // Account deletion state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  // Strava disconnect dialog state
  const [showDisconnectDialog, setShowDisconnectDialogState] = useState(false);

  // UI state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info'>('success');

  // NEW: HR Zone Menu state
  const [hrMenuAnchor, setHrMenuAnchor] = useState<null | HTMLElement>(null);

  // NEW: Sync History State
  const [showSyncHistory, setShowSyncHistory] = useState(false);
  const [syncHistory, setSyncHistory] = useState<SyncHistoryItem[]>([]);
  const [isSyncHistoryLoading, setIsSyncHistoryLoading] = useState(false);
  const [syncHistoryError, setSyncHistoryError] = useState<string | null>(null);
  const [expandedHistoryRow, setExpandedHistoryRow] = useState<number | null>(null);

  // NEW: Manual Sync State
  const [showManualSync, setShowManualSync] = useState(false);
  const [afterDays, setAfterDays] = useState<number>(30);
  const [isManualSyncing, setIsManualSyncing] = useState(false);

  // Fetch Sync History
  const fetchSyncHistory = async () => {
    try {
      setIsSyncHistoryLoading(true);
      const response = await apiClient.get<SyncHistoryResponse>(
        `sync/history?page=1&page_size=10`
      );

      // Parse metadata_info if it's a string
      const processedTasks = response.tasks.map(task => {
        if (typeof task.metadata_info === 'string') {
          try {
            return { ...task, metadata_info: JSON.parse(task.metadata_info as any) };
          } catch (e) {
            console.error('Failed to parse metadata_info for task', task.id, e);
            return task;
          }
        }
        return task;
      });

      setSyncHistory(processedTasks);
    } catch (err) {
      console.error('Failed to fetch sync history:', err);
      setSyncHistoryError('Failed to load sync history.');
    } finally {
      setIsSyncHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (showSyncHistory) {
      fetchSyncHistory();
    }
  }, [showSyncHistory]);

  const toggleSyncHistory = () => {
    setShowSyncHistory(!showSyncHistory);
    if (showManualSync) setShowManualSync(false); // Close manual sync if opening history
  };

  const toggleManualSync = () => {
    setShowManualSync(!showManualSync);
    if (showSyncHistory) setShowSyncHistory(false); // Close history if opening manual sync
  };


  const getStatusColor = (status: SyncHistoryItem['status']) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'active': return 'info';
      case 'pending': return 'warning';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: SyncHistoryItem['status']) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon fontSize="small" />;
      case 'failed': return <ErrorIcon fontSize="small" />;
      case 'active': return <SyncIcon fontSize="small" sx={{ animation: 'spin 2s linear infinite' }} />;
      case 'pending': return <PendingIcon fontSize="small" />;
      case 'cancelled': return <CancelIcon fontSize="small" />;
      default: return null;
    }
  };

  const formatDuration = (seconds?: number) => {
    if (seconds === undefined || seconds === null) return 'N/A';
    if (seconds < 1) {
      const milliseconds = Math.round(seconds * 1000);
      return `${milliseconds}ms`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`;
  };

  const formatErrorMessage = (task: SyncHistoryItem): string => {
    if (!task.error_message && (!task.metadata_info?.errors || task.metadata_info.errors.length === 0)) {
      return 'Unknown error';
    }
    if (task.error_message) return task.error_message;
    if (task.metadata_info?.errors && task.metadata_info.errors.length > 0) {
      return task.metadata_info.errors.join(', ');
    }
    return 'Sync failed';
  };

  // Convert days to months for display
  const daysToMonthsLabel = (days: number): string => {
    if (days < 30) {
      return `${days} day${days !== 1 ? 's' : ''}`;
    }
    const months = Math.round(days / 30);
    return `${months} month${months !== 1 ? 's' : ''}`;
  };

  const handleManualSync = async () => {
    try {
      setIsManualSyncing(true);
      setSnackbarMessage('Sync started. This may take a while...');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);

      // Call the direct sync endpoint
      await apiClient.post(
        `sync/direct-sync?after_days=${afterDays}`,
        {}
      );

      setSnackbarMessage('Sync completed successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      // Refresh history and data
      fetchSyncHistory();
      dispatch(baseApi.util.invalidateTags([
        { type: 'Activity', id: 'LIST' },
        'ActivityStats',
        'Analytics'
      ]));

    } catch (err: any) {
      console.error('Failed to trigger sync:', err);
      setSnackbarMessage(err.message || 'Failed to trigger sync.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsManualSyncing(false);
    }
  };



  // Initialize form data with user data from API
  useEffect(() => {
    if (userData) {
      const fullName = `${userData.first_name} ${userData.last_name}`;
      setProfileName(fullName);
      setProfileEmail(userData.email || '');
      setProfilePicture(userData.profile_picture_url || '');
      setCurrentRole(userData.role as UserRole);
      setIsStravaConnected(!!userData.strava_athlete_id);
      setStravaConnectionDate(userData.strava_connected_at || '');
      setEmailNotifications(userData.email_notifications);
      setSyncNotifications(userData.sync_notifications);
      setWeeklySummary(userData.weekly_summary);
      setProfileVisibility(userData.profile_visibility);
      setActivityVisibility(userData.activity_visibility);

      // Initialize new metrics
      setMaxHr(userData.max_hr ?? '');
      setWeight(userData.weight ?? '');
      setRestingHr(userData.resting_hr ?? '');
      setAge(userData.age ?? '');
      setVo2max(userData.vo2max ?? '');
      setOriginalMetricsData({
        maxHr: userData.max_hr ?? '',
        weight: userData.weight ?? '',
        restingHr: userData.resting_hr ?? '',
        age: userData.age ?? '',
        vo2max: userData.vo2max ?? ''
      });

      // Initialize new zones
      setFtpCycling(userData.ftp_cycling ?? '');
      setThresholdPaceRunning(userData.threshold_pace_running ?? '');
      setLactateThresholdHeartRate(userData.lactate_threshold_heart_rate ?? '');
      setOriginalZonesData({
        ftpCycling: userData.ftp_cycling ?? '',
        thresholdPaceRunning: userData.threshold_pace_running ?? '',
        lactateThresholdHeartRate: userData.lactate_threshold_heart_rate ?? ''
      });

      // Store original data for cancel functionality
      setOriginalProfileData({
        fullName,
        email: userData.email || '',
        profile_picture_url: userData.profile_picture_url || '',
      });
    }
  }, [userData]);

  useEffect(() => {
    if (searchParams.get('prompt_metrics') === 'true') {
      setSnackbarMessage('For best results please fill your Max HR and Lactate Threshold HR!');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
      setShowVO2maxEstimateDialog(true);
    }

    if (searchParams.get('sync') === 'true') {
      setShowManualSync(true);
    }
  }, [searchParams]);

  // Handlers
  const handleProfileEdit = () => {
    setIsEditingProfile(true);
  };

  const handleProfileSave = async () => {
    try {
      const [firstName, ...lastNameParts] = profileName.split(' ');
      const lastName = lastNameParts.join(' ');

      await updateUserProfile({
        first_name: firstName,
        last_name: lastName,
        email: profileEmail,
        profile_picture_url: profilePicture,
      }).unwrap();

      setIsEditingProfile(false);
      setSnackbarMessage('Profile updated successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      // Update original data
      setOriginalProfileData({
        fullName: profileName,
        email: profileEmail,
        profile_picture_url: profilePicture,
      });

      // Refetch user data to get the latest
      refetch();
    } catch (error) {
      setSnackbarMessage('Failed to update profile');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleProfileCancel = () => {
    setIsEditingProfile(false);
    // Reset to original values
    setProfileName(originalProfileData.fullName);
    setProfileEmail(originalProfileData.email);
    setProfilePicture(originalProfileData.profile_picture_url);
  };

  const handleProfilePictureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicture(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // NEW: Metric Handlers
  const handleMetricsEdit = () => {
    setIsEditingMetrics(true);
  };

  const handleMetricsSave = async () => {
    try {
      await updateUserProfile({
        max_hr: maxHr === '' ? null : Number(maxHr),
        weight: weight === '' ? null : Number(weight),
        resting_hr: restingHr === '' ? null : Number(restingHr),
        age: age === '' ? null : Number(age),
        vo2max: vo2max === '' ? null : Number(vo2max),
      }).unwrap();

      setIsEditingMetrics(false);
      setSnackbarMessage('Athlete metrics updated successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      setOriginalMetricsData({ maxHr, weight, restingHr, age, vo2max });
      refetch();
    } catch (error) {
      setSnackbarMessage('Failed to update metrics');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleMetricsCancel = () => {
    setIsEditingMetrics(false);
    setMaxHr(originalMetricsData.maxHr);
    setWeight(originalMetricsData.weight);
    setRestingHr(originalMetricsData.restingHr);
    setAge(originalMetricsData.age);
    setVo2max(originalMetricsData.vo2max);
  };

  // NEW: Zones Handlers
  const handleZonesEdit = () => {
    setIsEditingZones(true);
  };

  const handleZonesSave = async () => {
    try {
      await updateUserProfile({
        ftp_cycling: ftpCycling === '' ? null : Number(ftpCycling),
        threshold_pace_running: thresholdPaceRunning === '' ? null : Number(thresholdPaceRunning),
        lactate_threshold_heart_rate: lactateThresholdHeartRate === '' ? null : Number(lactateThresholdHeartRate),
      }).unwrap();

      setIsEditingZones(false);
      setSnackbarMessage('Performance zones updated successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      setOriginalZonesData({ ftpCycling, thresholdPaceRunning, lactateThresholdHeartRate });
      refetch();
    } catch (error) {
      setSnackbarMessage('Failed to update zones');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleZonesCancel = () => {
    setIsEditingZones(false);
    setFtpCycling(originalZonesData.ftpCycling);
    setThresholdPaceRunning(originalZonesData.thresholdPaceRunning);
    setLactateThresholdHeartRate(originalZonesData.lactateThresholdHeartRate);
  };

  // NEW: Zone Management Handlers
  const paceToDecimal = (pace: string) => {
    const [m, s] = pace.split(':').map(Number);
    return m + s / 60;
  };

  const decimalToPace = (decimal: number) => {
    const m = Math.floor(decimal);
    const s = Math.round((decimal - m) * 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleHRZoneSave = async (updatedZones: any[]) => {
    try {
      await saveHRZones({
        method: 'max_hr', // Default to max_hr if manual
        params: { max_hr: Number(maxHr) },
        zones: updatedZones.map(z => ({
          zone: z.id,
          name: z.name,
          min_hr: z.minValue,
          max_hr: z.maxValue
        }))
      }).unwrap();
      setSnackbarMessage('Heart rate zones updated');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      refetchHRZones();
    } catch (err) {
      setSnackbarMessage('Failed to save heart rate zones');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handlePaceZoneSave = async (updatedZones: any[]) => {
    try {
      await savePaceZones({
        reference_pace: decimalToPace(Number(thresholdPaceRunning) || 5),
        units: 'km',
        zones: updatedZones.map(z => ({
          zone: z.id,
          name: z.name,
          min_pace: decimalToPace(z.minValue),
          max_pace: decimalToPace(z.maxValue)
        }))
      }).unwrap();
      setSnackbarMessage('Pace zones updated');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      refetchPaceZones();
    } catch (err) {
      setSnackbarMessage('Failed to save pace zones');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handlePrefillHRZones = async (preferredMethod?: 'max_hr' | 'hrr' | 'lthr') => {
    if (!maxHr && preferredMethod !== 'lthr') {
      setSnackbarMessage('Please set your Max Heart Rate first');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    if (preferredMethod === 'lthr' && !lactateThresholdHeartRate) {
      setSnackbarMessage('Please set your Lactate Threshold HR first');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    try {
      let method: 'max_hr' | 'hrr' | 'lthr' = preferredMethod || (restingHr ? 'hrr' : 'max_hr');
      let params = {};

      if (method === 'hrr') {
        params = { max_hr: Number(maxHr), resting_hr: Number(restingHr) };
      } else if (method === 'lthr') {
        params = { lthr: Number(lactateThresholdHeartRate) };
      } else {
        params = { max_hr: Number(maxHr) };
      }

      await calculateHRZones({
        method,
        params,
      }).unwrap();

      const methodNames = {
        'max_hr': 'Max Heart Rate',
        'hrr': 'Heart Rate Reserve',
        'lthr': 'Lactate Threshold'
      };

      setSnackbarMessage(`Zones pre-filled using ${methodNames[method]} method`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      refetchHRZones();
    } catch (err) {
      setSnackbarMessage('Failed to calculate zones');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handlePrefillPaceZones = async () => {
    if (!thresholdPaceRunning) {
      setSnackbarMessage('Please set your Threshold Pace first');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    try {
      await calculatePaceZones({
        params: {
          reference_pace: decimalToPace(Number(thresholdPaceRunning)),
          units: 'km',
        }
      }).unwrap();

      setSnackbarMessage('Pace zones pre-filled based on threshold pace');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      refetchPaceZones();
    } catch (err) {
      setSnackbarMessage('Failed to calculate pace zones');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleThemeChange = (newTheme: 'light' | 'dark') => {
    dispatch(setTheme(newTheme));
    localStorage.setItem('theme-preference', newTheme);
    setSnackbarMessage(`Theme changed to ${newTheme} mode`);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const handleRoleChangeRequest = () => {
    setShowRoleChangeDialog(true);
  };

  const handleRoleChangeSubmit = async () => {
    try {
      if (!requestedRole) {
        setSnackbarMessage('Please select a role');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      await requestRoleChange({ requestedRole }).unwrap();
      setShowRoleChangeDialog(false);
      setSnackbarMessage('Role change request submitted successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setRequestedRole(null);
      setRoleChangeReason('');
    } catch (error) {
      setSnackbarMessage('Failed to submit role change request');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleStravaReconnect = () => {
    setSnackbarMessage('Redirecting to Strava for reconnection...');
    setSnackbarSeverity('info');
    setSnackbarOpen(true);
    // Redirect to Strava OAuth flow
    window.location.href = '/api/v1/auth/strava';
  };

  const handleStravaDisconnect = () => {
    setShowDisconnectDialogState(true);
  };

  const handleStravaDisconnectConfirm = async () => {
    try {
      // This would need an API endpoint to disconnect Strava
      // For now, we'll just update the UI state
      setIsStravaConnected(false);
      setShowDisconnectDialogState(false);
      setSnackbarMessage('Strava account disconnected');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage('Failed to disconnect Strava account');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleNotificationPreferenceChange = async (preference: string, value: boolean) => {
    try {
      const updatedPreferences = {
        email_notifications: preference === 'email' ? value : emailNotifications,
        sync_notifications: preference === 'sync' ? value : syncNotifications,
        weekly_summary: preference === 'weekly' ? value : weeklySummary,
      };

      await updateNotificationPreferences(updatedPreferences).unwrap();

      // Update local state
      switch (preference) {
        case 'email':
          setEmailNotifications(value);
          break;
        case 'sync':
          setSyncNotifications(value);
          break;
        case 'weekly':
          setWeeklySummary(value);
          break;
      }

      setSnackbarMessage('Notification preferences updated');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage('Failed to update notification preferences');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleAccountDelete = () => {
    setShowDeleteDialog(true);
  };

  const handleAccountDeleteConfirm = async () => {
    if (deleteConfirmation === 'DELETE') {
      try {
        await deleteAccount().unwrap();
        setShowDeleteDialog(false);
        setSnackbarMessage('Account deletion initiated. You will be logged out shortly.');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);

        // Perform account deletion
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
      } catch (error) {
        setSnackbarMessage('Failed to delete account');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } else {
      setSnackbarMessage('Please type DELETE to confirm account deletion');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const getRoleLabel = (role: string): string => {
    switch (role) {
      case 'athlete':
        return 'Athlete';
      case 'trainer':
        return 'Trainer';
      case 'both':
        return 'Athlete & Trainer';
      default:
        return role;
    }
  };

  // Show loading state while fetching user data
  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          User Settings
        </Typography>
        <Typography>Loading user data...</Typography>
      </Container>
    );
  }

  // Show error state if API call fails
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          User Settings
        </Typography>
        <Alert severity="error">
          Failed to load user data. Please try again later.
        </Alert>
        <Button variant="contained" onClick={() => refetch()} sx={{ mt: 2 }}>
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: { xs: 4, md: 8 } }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }}>
            Settings
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500, opacity: 0.8 }}>
            Configure your profile, performance metrics, and app preferences
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Left Column: Profile & Main Metrics */}
          <Grid item xs={12} lg={8}>
            <Stack spacing={4}>
              {/* Profile Information Section */}
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  background: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'linear-gradient(145deg, rgba(30, 30, 40, 0.6) 0%, rgba(20, 20, 25, 0.4) 100%)'
                      : 'linear-gradient(145deg, rgba(255, 255, 255, 1) 0%, rgba(245, 247, 250, 1) 100%)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        p: 1.25,
                        borderRadius: 2.5,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        display: 'flex',
                        alignItems: 'center',
                        color: 'primary.main'
                      }}
                    >
                      <PersonIcon />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                      Profile Details
                    </Typography>
                  </Box>
                  {!isEditingProfile && (
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={handleProfileEdit}
                      sx={{
                        borderRadius: 2.5,
                        px: 3,
                        fontWeight: 600,
                        textTransform: 'none',
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                      }}
                    >
                      Edit Profile
                    </Button>
                  )}
                </Box>

                <Grid container spacing={5} alignItems="center">
                  <Grid item xs={12} md={4} lg={3.5} sx={{ textAlign: 'center' }}>
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                      <Avatar
                        src={profilePicture}
                        alt={profileName}
                        sx={{
                          width: 160,
                          height: 160,
                          mx: 'auto',
                          mb: 3,
                          boxShadow: `0 12px 32px ${alpha(theme.palette.common.black, 0.12)}`,
                          border: '6px solid',
                          borderColor: 'background.paper',
                        }}
                      />
                      {isEditingProfile && (
                        <IconButton
                          color="primary"
                          component="label"
                          sx={{
                            position: 'absolute',
                            bottom: 25,
                            right: 5,
                            bgcolor: 'background.paper',
                            boxShadow: 4,
                            '&:hover': { bgcolor: 'primary.main', color: 'white' },
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}
                          size="medium"
                        >
                          <PhotoCameraIcon fontSize="small" />
                          <input type="file" hidden accept="image/*" onChange={handleProfilePictureChange} />
                        </IconButton>
                      )}
                    </Box>
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label={getRoleLabel(currentRole)}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{
                          fontWeight: 700,
                          px: 1.5,
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          borderColor: alpha(theme.palette.primary.main, 0.2),
                          height: 28,
                          textTransform: 'uppercase',
                          fontSize: '0.65rem',
                          letterSpacing: 0.5
                        }}
                      />
                      <Button
                        size="small"
                        onClick={handleRoleChangeRequest}
                        sx={{
                          ml: 1.5,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          minWidth: 'auto',
                          p: 0,
                          textTransform: 'none',
                          color: 'text.secondary',
                          '&:hover': { color: 'primary.main', bgcolor: 'transparent' }
                        }}
                      >
                        Change
                      </Button>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={8.5}>
                    <Stack spacing={3}>
                      <TextField
                        fullWidth
                        label="Display Name"
                        value={profileName}
                        onChange={(e) => setProfileName(e.target.value)}
                        disabled={!isEditingProfile}
                        variant={isEditingProfile ? 'outlined' : 'standard'}
                        InputProps={{
                          disableUnderline: !isEditingProfile,
                          sx: { fontWeight: 600, fontSize: isEditingProfile ? 'inherit' : '1.25rem' }
                        }}
                      />
                      <TextField
                        fullWidth
                        label="Email Address"
                        value={profileEmail}
                        onChange={(e) => setProfileEmail(e.target.value)}
                        disabled={!isEditingProfile}
                        variant={isEditingProfile ? 'outlined' : 'standard'}
                        type="email"
                        InputProps={{ disableUnderline: !isEditingProfile }}
                      />

                      {isEditingProfile && (
                        <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
                          <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleProfileSave}
                            sx={{ borderRadius: 2.5, px: 4, fontWeight: 700 }}
                          >
                            Save Changes
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<CancelIcon />}
                            onClick={handleProfileCancel}
                            sx={{ borderRadius: 2.5, px: 3, fontWeight: 600 }}
                          >
                            Cancel
                          </Button>
                        </Box>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>

              {/* Athlete Metrics Section */}
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: alpha(theme.palette.background.paper, 0.4),
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        p: 1.25,
                        borderRadius: 2.5,
                        bgcolor: alpha(theme.palette.secondary.main, 0.08),
                        display: 'flex',
                        alignItems: 'center',
                        color: 'secondary.main'
                      }}
                    >
                      <MetricsIcon />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                      Athlete Metrics
                    </Typography>
                  </Box>
                  {!isEditingMetrics && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<EditIcon />}
                      size="small"
                      onClick={handleMetricsEdit}
                      sx={{
                        borderRadius: 2.5,
                        px: 3,
                        fontWeight: 600,
                        textTransform: 'none',
                        borderColor: alpha(theme.palette.secondary.main, 0.3),
                        '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.05) }
                      }}
                    >
                      Update Metrics
                    </Button>
                  )}
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Max Heart Rate"
                      placeholder="e.g. 190"
                      value={String(maxHr)}
                      onChange={(e) => setMaxHr(e.target.value === '' ? '' : Number(e.target.value))}
                      disabled={!isEditingMetrics}
                      type="number"
                      variant="outlined"
                      InputProps={{
                        endAdornment: <Typography variant="caption" color="text.secondary">BPM</Typography>,
                        sx: { borderRadius: 3 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Current Weight"
                      placeholder="e.g. 75"
                      value={String(weight)}
                      onChange={(e) => setWeight(e.target.value === '' ? '' : Number(e.target.value))}
                      disabled={!isEditingMetrics}
                      type="number"
                      variant="outlined"
                      InputProps={{
                        endAdornment: <Typography variant="caption" color="text.secondary">KG</Typography>,
                        sx: { borderRadius: 3 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Resting Heart Rate"
                      placeholder="e.g. 60"
                      value={String(restingHr)}
                      onChange={(e) => setRestingHr(e.target.value === '' ? '' : Number(e.target.value))}
                      disabled={!isEditingMetrics}
                      type="number"
                      variant="outlined"
                      InputProps={{
                        endAdornment: <Typography variant="caption" color="text.secondary">BPM</Typography>,
                        sx: { borderRadius: 3 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Age"
                      placeholder="e.g. 30"
                      value={String(age)}
                      onChange={(e) => setAge(e.target.value === '' ? '' : Number(e.target.value))}
                      disabled={!isEditingMetrics}
                      type="number"
                      variant="outlined"
                      InputProps={{
                        sx: { borderRadius: 3 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="VO2 Max"
                      placeholder="e.g. 50.5"
                      value={String(vo2max)}
                      onChange={(e) => setVo2max(e.target.value === '' ? '' : Number(e.target.value))}
                      disabled={!isEditingMetrics}
                      type="number"
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <Button
                            size="small"
                            onClick={() => setShowVO2maxEstimateDialog(true)}
                            sx={{ fontSize: '0.7rem', minWidth: 'auto', ml: 1 }}
                          >
                            Estimate
                          </Button>
                        ),
                        sx: { borderRadius: 3 }
                      }}
                      helperText="Calculated or tested VO2 Max"
                    />
                  </Grid>
                </Grid>

                {isEditingMetrics && (
                  <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<SaveIcon />}
                      onClick={handleMetricsSave}
                      sx={{ borderRadius: 2 }}
                    >
                      Save Metrics
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleMetricsCancel}
                      sx={{ borderRadius: 2 }}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Paper>

              {/* Performance Zones Section */}
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: alpha(theme.palette.background.paper, 0.4),
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        p: 1.25,
                        borderRadius: 2.5,
                        bgcolor: alpha(theme.palette.info.main, 0.08),
                        display: 'flex',
                        alignItems: 'center',
                        color: 'info.main'
                      }}
                    >
                      <ZonesIcon />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                      Performance Zones
                    </Typography>
                  </Box>
                  {!isEditingZones && (
                    <Button
                      variant="outlined"
                      color="info"
                      startIcon={<EditIcon />}
                      size="small"
                      onClick={handleZonesEdit}
                      sx={{
                        borderRadius: 2.5,
                        px: 3,
                        fontWeight: 600,
                        textTransform: 'none',
                        borderColor: alpha(theme.palette.info.main, 0.3),
                        '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.05) }
                      }}
                    >
                      Adjust Zones
                    </Button>
                  )}
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Cycling FTP"
                      value={String(ftpCycling)}
                      onChange={(e) => setFtpCycling(e.target.value === '' ? '' : Number(e.target.value))}
                      disabled={!isEditingZones}
                      type="number"
                      variant="outlined"
                      InputProps={{
                        endAdornment: <Typography variant="caption" color="text.secondary">Watts</Typography>,
                        sx: { borderRadius: 3 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Threshold Pace"
                      value={String(thresholdPaceRunning)}
                      onChange={(e) => setThresholdPaceRunning(e.target.value === '' ? '' : Number(e.target.value))}
                      disabled={!isEditingZones}
                      type="number"
                      variant="outlined"
                      InputProps={{
                        endAdornment: <Typography variant="caption" color="text.secondary">min/km</Typography>,
                        sx: { borderRadius: 3 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Lactate Threshold HR"
                      value={String(lactateThresholdHeartRate)}
                      onChange={(e) => setLactateThresholdHeartRate(e.target.value === '' ? '' : Number(e.target.value))}
                      disabled={!isEditingZones}
                      type="number"
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>BPM</Typography>
                            <Button
                              size="small"
                              onClick={() => setShowLTHREstimateDialog(true)}
                              sx={{ fontSize: '0.7rem', minWidth: 'auto' }}
                            >
                              Estimate
                            </Button>
                          </Box>
                        ),
                        sx: { borderRadius: 3 }
                      }}
                    />
                  </Grid>
                </Grid>

                {isEditingZones && (
                  <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                    <Button
                      variant="contained"
                      color="info"
                      startIcon={<SaveIcon />}
                      onClick={handleZonesSave}
                      sx={{ borderRadius: 2 }}
                    >
                      Save Zones
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleZonesCancel}
                      sx={{ borderRadius: 2 }}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Paper>

              {/* NEW: Heart Rate & Pace Zones Table Section */}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3.5,
                    flex: 1,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, opacity: 0.9, letterSpacing: -0.2 }}>
                      Intensity Distribution
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<AutoFixIcon />}
                      onClick={(e) => setHrMenuAnchor(e.currentTarget)}
                      disabled={isCalculatingHR}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {isCalculatingHR ? 'Calculating...' : 'Pre-fill Zones'}
                    </Button>
                    <Menu
                      anchorEl={hrMenuAnchor}
                      open={Boolean(hrMenuAnchor)}
                      onClose={() => setHrMenuAnchor(null)}
                    >
                      <MenuItem onClick={() => { handlePrefillHRZones('max_hr'); setHrMenuAnchor(null); }}>
                        Max Heart Rate (%)
                      </MenuItem>
                      <MenuItem onClick={() => { handlePrefillHRZones('hrr'); setHrMenuAnchor(null); }} disabled={!restingHr}>
                        Heart Rate Reserve (Karvonen)
                      </MenuItem>
                      <MenuItem onClick={() => { handlePrefillHRZones('lthr'); setHrMenuAnchor(null); }} disabled={!lactateThresholdHeartRate}>
                        Lactate Threshold (Joe Friel)
                      </MenuItem>
                    </Menu>
                  </Box>
                  <ZonesTable
                    zoneType="heartRate"
                    zones={hrZones?.zones.map(z => ({
                      id: z.zone,
                      name: z.name,
                      minValue: z.min_hr,
                      maxValue: z.max_hr,
                      description: ''
                    })) || []}
                    onSave={handleHRZoneSave}
                    editable={true}
                    maxHr={Number(maxHr) || undefined}
                    lactateThresholdHr={Number(lactateThresholdHeartRate) || undefined}
                  />
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 3.5,
                    flex: 1,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, opacity: 0.9, letterSpacing: -0.2 }}>
                      Pace Guidance
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<AutoFixIcon />}
                      onClick={handlePrefillPaceZones}
                      disabled={isCalculatingPace}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {isCalculatingPace ? 'Calculating...' : 'Sync with Pace'}
                    </Button>
                  </Box>
                  <ZonesTable
                    zoneType="pace"
                    unit="min/km"
                    zones={paceZones?.zones.map(z => ({
                      id: z.zone,
                      name: z.name,
                      minValue: paceToDecimal(z.min_pace),
                      maxValue: paceToDecimal(z.max_pace),
                      description: ''
                    })) || []}
                    onSave={handlePaceZoneSave}
                    editable={true}
                  />
                </Paper>
              </Stack>
            </Stack>
          </Grid>

          {/* Right Column: Preferences & Integrations */}
          <Grid item xs={12} lg={4}>
            <Stack spacing={4}>
              {/* Strava Integration Card */}
              {/* Strava Integration Card */}
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: isStravaConnected ? alpha(theme.palette.success.main, 0.2) : alpha(theme.palette.warning.main, 0.2),
                  bgcolor: isStravaConnected ? alpha(theme.palette.success.main, 0.05) : alpha(theme.palette.warning.main, 0.05),
                  backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 100%)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      bgcolor: isStravaConnected ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.warning.main, 0.1),
                      color: isStravaConnected ? 'success.main' : 'warning.main',
                      display: 'flex'
                    }}
                  >
                    <SyncIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.2 }}>
                    Strava Sync
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Chip
                    icon={isStravaConnected ? <CheckCircleIcon /> : <WarningIcon />}
                    label={isStravaConnected ? 'Authenticated' : 'Not Connected'}
                    color={isStravaConnected ? 'success' : 'warning'}
                    sx={{ fontWeight: 600, mb: 1, height: 32 }}
                  />
                  {isStravaConnected && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Linked on {stravaConnectionDate ? new Date(stravaConnectionDate).toLocaleDateString() : 'N/A'}
                    </Typography>
                  )}
                </Box>

                <Stack direction="row" spacing={1.5}>
                  {isStravaConnected ? (
                    <>
                      <Button
                        variant="contained"
                        onClick={handleStravaReconnect}
                        fullWidth
                        sx={{ bgcolor: 'background.paper', color: 'text.primary', '&:hover': { bgcolor: 'background.default' }, borderRadius: 2 }}
                      >
                        Reconnect
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={toggleSyncHistory}
                        startIcon={<HistoryIcon />}
                        sx={{
                          borderRadius: 2,
                          borderColor: 'divider',
                          color: showSyncHistory ? 'primary.main' : 'text.secondary',
                          bgcolor: showSyncHistory ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                          minWidth: 'auto',
                          px: 2
                        }}
                      >
                        History
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={toggleManualSync}
                        startIcon={<SyncIcon />}
                        sx={{
                          borderRadius: 2,
                          borderColor: 'divider',
                          color: showManualSync ? 'primary.main' : 'text.secondary',
                          bgcolor: showManualSync ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                          minWidth: 'auto',
                          px: 2
                        }}
                      >
                        Sync
                      </Button>
                      <IconButton
                        color="error"
                        onClick={handleStravaDisconnect}
                        sx={{ bgcolor: 'rgba(211, 47, 47, 0.1)', borderRadius: 2 }}
                      >
                        <LinkOffIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleStravaReconnect}
                      fullWidth
                      startIcon={<LinkIcon />}
                      sx={{ borderRadius: 2, bgcolor: '#FC4C02', '&:hover': { bgcolor: '#E34402' } }}
                    >
                      Connect Strava
                    </Button>
                  )}
                </Stack>

                {/* Manual Sync Section */}
                <Collapse in={showManualSync}>
                  <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 1 }}>
                      Manual Sync
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
                      Sync activities from Strava. Select how far back to look for new or updated activities.
                    </Typography>

                    <Box sx={{ px: 1, mb: 2 }}>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        Period: {daysToMonthsLabel(afterDays)}
                      </Typography>
                      <Slider
                        value={afterDays}
                        onChange={(_, value) => setAfterDays(value as number)}
                        min={1}
                        max={365}
                        step={1}
                        marks={[
                          { value: 7, label: '1w' },
                          { value: 30, label: '1m' },
                          { value: 90, label: '3m' },
                          { value: 180, label: '6m' },
                          { value: 365, label: '1y' },
                        ]}
                        valueLabelDisplay="auto"
                        valueLabelFormat={daysToMonthsLabel}
                        disabled={isManualSyncing}
                        sx={{
                          '& .MuiSlider-markLabel': { fontSize: '0.7rem' }
                        }}
                      />
                    </Box>

                    <Button
                      variant="contained"
                      onClick={handleManualSync}
                      disabled={isManualSyncing}
                      fullWidth
                      startIcon={isManualSyncing ? <SyncIcon sx={{ animation: 'spin 2s linear infinite' }} /> : <SyncIcon />}
                      sx={{ borderRadius: 2, mt: 2 }}
                    >
                      {isManualSyncing ? 'Syncing...' : 'Start Sync'}
                    </Button>
                  </Box>
                </Collapse>

                {/* Sync History List */}
                <Collapse in={showSyncHistory}>
                  <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                        Recent Sync Activity
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<SyncIcon fontSize="small" />}
                        onClick={fetchSyncHistory}
                        disabled={isSyncHistoryLoading}
                        sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                      >
                        Refresh
                      </Button>
                    </Box>

                    {isSyncHistoryLoading ? (
                      <LinearProgress sx={{ borderRadius: 1 }} />
                    ) : syncHistory.length === 0 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 2 }}>
                        No sync history found.
                      </Typography>
                    ) : (
                      <Stack spacing={1.5}>
                        {syncHistory.map((task) => (
                          <Paper
                            key={task.id}
                            elevation={0}
                            sx={{
                              p: 1.5,
                              bgcolor: alpha(theme.palette.background.default, 0.5),
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ color: `${getStatusColor(task.status)}.main`, display: 'flex' }}>
                                  {getStatusIcon(task.status)}
                                </Box>
                                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                                  {task.status}
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                {formatDistanceToNow(new Date(task.started_at), { addSuffix: true })}
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {task.activities_synced} activities • {formatDuration(task.metadata_info?.duration_seconds)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {task.source}
                              </Typography>
                            </Box>

                            {/* Error Details */}
                            {(task.status === 'failed' || task.error_message) && (
                              <Box>
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => setExpandedHistoryRow(expandedHistoryRow === task.id ? null : task.id)}
                                  endIcon={
                                    <ExpandMoreIcon
                                      fontSize="small"
                                      sx={{
                                        transform: expandedHistoryRow === task.id ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s',
                                        fontSize: '1rem'
                                      }}
                                    />
                                  }
                                  sx={{
                                    fontSize: '0.65rem',
                                    p: 0,
                                    minWidth: 'auto',
                                    textTransform: 'none',
                                    '&:hover': { bgcolor: 'transparent' }
                                  }}
                                >
                                  Error Details
                                </Button>
                                <Collapse in={expandedHistoryRow === task.id}>
                                  <Box sx={{ mt: 1, p: 1, bgcolor: alpha(theme.palette.error.main, 0.1), borderRadius: 1 }}>
                                    <Typography variant="caption" color="error" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>
                                      {formatErrorMessage(task)}
                                    </Typography>
                                  </Box>
                                </Collapse>
                              </Box>
                            )}
                          </Paper>
                        ))}
                      </Stack>
                    )}
                  </Box>
                  <style>
                    {`
                      @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                      }
                    `}
                  </style>
                </Collapse>
              </Paper>

              {/* Appearance Section */}
              <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(theme.palette.text.secondary, 0.1), color: 'text.secondary', display: 'flex' }}>
                    <PaletteIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.2 }}>Appearance</Typography>
                </Box>

                <Box sx={{ bgcolor: alpha(theme.palette.action.hover, 0.5), borderRadius: 3, p: 0.75, display: 'flex', mb: 3 }}>
                  <Button
                    fullWidth
                    variant={currentTheme === 'light' ? 'contained' : 'text'}
                    onClick={() => handleThemeChange('light')}
                    startIcon={<Brightness7Icon />}
                    sx={{
                      py: 1,
                      borderRadius: 2.5,
                      textTransform: 'none',
                      fontWeight: 700,
                      boxShadow: currentTheme === 'light' ? 2 : 'none',
                      color: currentTheme === 'light' ? 'primary.main' : 'text.secondary',
                      bgcolor: currentTheme === 'light' ? 'background.paper' : 'transparent',
                      '&:hover': { bgcolor: currentTheme === 'light' ? 'background.paper' : alpha(theme.palette.primary.main, 0.05) }
                    }}
                  >
                    Light
                  </Button>
                  <Button
                    fullWidth
                    variant={currentTheme === 'dark' ? 'contained' : 'text'}
                    onClick={() => handleThemeChange('dark')}
                    startIcon={<Brightness4Icon />}
                    sx={{
                      py: 1,
                      borderRadius: 2.5,
                      textTransform: 'none',
                      fontWeight: 700,
                      boxShadow: currentTheme === 'dark' ? 2 : 'none',
                      color: currentTheme === 'dark' ? 'primary.main' : 'text.secondary',
                      bgcolor: currentTheme === 'dark' ? 'background.paper' : 'transparent',
                      '&:hover': { bgcolor: currentTheme === 'dark' ? 'background.paper' : alpha(theme.palette.primary.main, 0.05) }
                    }}
                  >
                    Dark
                  </Button>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Choose between light and dark modes for your interface.
                </Typography>
              </Paper>

              {/* Notification Preferences */}
              <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(theme.palette.text.secondary, 0.1), color: 'text.secondary', display: 'flex' }}>
                    <NotificationsIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.2 }}>Notifications</Typography>
                </Box>

                <Stack spacing={2.5}>
                  {[
                    { id: 'email', label: 'Email Reports', icon: <EmailIcon fontSize="small" />, value: emailNotifications },
                    { id: 'sync', label: 'Sync Status', icon: <SyncIcon fontSize="small" />, value: syncNotifications },
                    { id: 'weekly', label: 'Weekly Summary', icon: <NotificationsIcon fontSize="small" />, value: weeklySummary },
                  ].map((item) => (
                    <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {item.icon}
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.label}</Typography>
                      </Box>
                      <Switch
                        size="small"
                        checked={item.value}
                        onChange={(e) => handleNotificationPreferenceChange(item.id, e.target.checked)}
                      />
                    </Box>
                  ))}
                </Stack>
              </Paper>

              {/* Privacy Settings */}
              <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(theme.palette.text.secondary, 0.1), color: 'text.secondary', display: 'flex' }}>
                    <SecurityIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.2 }}>Privacy</Typography>
                </Box>

                <Stack spacing={3}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <VisibilityIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Public Profile</Typography>
                    </Box>
                    <Switch
                      size="small"
                      checked={profileVisibility === 'public'}
                      onChange={(e) => setProfileVisibility(e.target.checked ? 'public' : 'private')}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <LockIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Share Activities</Typography>
                    </Box>
                    <Switch
                      size="small"
                      checked={activityVisibility !== 'private'}
                      onChange={(e) => setActivityVisibility(e.target.checked ? 'followers' : 'private')}
                    />
                  </Box>
                </Stack>
              </Paper>

              {/* Danger Zone */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'error.light',
                  bgcolor: 'rgba(211, 47, 47, 0.04)',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main', mb: 2 }}>
                  Danger Zone
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                  Irreversible account actions. Please proceed with caution.
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  fullWidth
                  startIcon={<DeleteIcon />}
                  onClick={handleAccountDelete}
                  sx={{ borderRadius: 2 }}
                >
                  Delete Account
                </Button>
              </Paper>
            </Stack>
          </Grid>
        </Grid>

        {/* Role Change Dialog */}
        <Dialog
          open={showRoleChangeDialog}
          onClose={() => setShowRoleChangeDialog(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
        >
          <DialogTitle sx={{ fontWeight: 800, letterSpacing: -0.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', display: 'flex' }}>
              <EditIcon sx={{ fontSize: 20 }} />
            </Box>
            Request Role Change
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 3, fontWeight: 500 }}>
              Select the role you'd like to transition to. Our team will review your request.
            </DialogContentText>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              {[
                { id: 'athlete', label: 'Athlete', icon: <PersonIcon /> },
                { id: 'trainer', label: 'Trainer', icon: <MetricsIcon /> },
                { id: 'both', label: 'Athlete & Trainer', icon: <SettingsIcon /> },
              ].map((role) => (
                <Button
                  key={role.id}
                  fullWidth
                  variant={requestedRole === role.id ? 'contained' : 'outlined'}
                  onClick={() => setRequestedRole(role.id as any)}
                  startIcon={role.icon}
                  sx={{
                    py: 1.5,
                    borderRadius: 2.5,
                    justifyContent: 'flex-start',
                    px: 3,
                    fontWeight: 700,
                    textTransform: 'none',
                    borderColor: requestedRole === role.id ? 'primary.main' : alpha(theme.palette.divider, 0.5),
                    bgcolor: requestedRole === role.id ? 'primary.main' : 'transparent',
                    '&:hover': {
                      bgcolor: requestedRole === role.id ? 'primary.main' : alpha(theme.palette.primary.main, 0.05)
                    }
                  }}
                >
                  {role.label}
                </Button>
              ))}
            </Box>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Why the change?"
              placeholder="Tell us about your coaching experience..."
              value={roleChangeReason}
              onChange={(e) => setRoleChangeReason(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button onClick={() => setShowRoleChangeDialog(false)} color="inherit" sx={{ fontWeight: 600 }}>Cancel</Button>
            <Button
              onClick={handleRoleChangeSubmit}
              variant="contained"
              disabled={!requestedRole}
              sx={{ borderRadius: 2.5, px: 4, fontWeight: 700, textTransform: 'none' }}
            >
              Submit Request
            </Button>
          </DialogActions>
        </Dialog>

        {/* VO2max Estimation Dialog */}
        <Dialog
          open={showVO2maxEstimateDialog}
          onClose={() => {
            setShowVO2maxEstimateDialog(false);
            setShouldFetchEstimate(false);
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
        >
          <DialogTitle sx={{ fontWeight: 800, letterSpacing: -0.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(theme.palette.secondary.main, 0.1), color: 'secondary.main', display: 'flex' }}>
              <MetricsIcon sx={{ fontSize: 20 }} />
            </Box>
            VO2 Max Estimation
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 3, fontWeight: 500 }}>
              Provide your metrics to calculate a personalized VO2 Max estimate.
            </DialogContentText>

            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Age"
                  type="number"
                  value={age}
                  onChange={(e) => {
                    setAge(e.target.value === '' ? '' : Number(e.target.value));
                    setShouldFetchEstimate(false);
                  }}
                  variant="outlined"
                  InputProps={{ sx: { borderRadius: 3 } }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Max HR"
                  type="number"
                  value={maxHr}
                  onChange={(e) => {
                    setMaxHr(e.target.value === '' ? '' : Number(e.target.value));
                    setShouldFetchEstimate(false);
                  }}
                  variant="outlined"
                  InputProps={{ sx: { borderRadius: 3 }, endAdornment: <Typography variant="caption">BPM</Typography> }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Resting HR"
                  type="number"
                  value={restingHr === '' ? 60 : restingHr}
                  onChange={(e) => {
                    setRestingHr(e.target.value === '' ? '' : Number(e.target.value));
                    setShouldFetchEstimate(false);
                  }}
                  variant="outlined"
                  InputProps={{ sx: { borderRadius: 3 }, endAdornment: <Typography variant="caption">BPM</Typography> }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Weight"
                  type="number"
                  value={weight}
                  onChange={(e) => {
                    setWeight(e.target.value === '' ? '' : Number(e.target.value));
                    setShouldFetchEstimate(false);
                  }}
                  variant="outlined"
                  InputProps={{ sx: { borderRadius: 3 }, endAdornment: <Typography variant="caption">KG</Typography> }}
                />
              </Grid>
            </Grid>

            {!shouldFetchEstimate || !estimateData ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  onClick={() => {
                    if (restingHr === '') setRestingHr(60);
                    setShouldFetchEstimate(true);
                  }}
                  disabled={!age || !maxHr}
                  sx={{ borderRadius: 3, px: 4, fontWeight: 700, textTransform: 'none' }}
                >
                  Calculate Estimate
                </Button>
                {(!age || !maxHr) && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, fontWeight: 500 }}>
                    Age and Max HR are required. Resting HR defaults to 60.
                  </Typography>
                )}
              </Box>
            ) : (
              <>
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, bgcolor: alpha(theme.palette.secondary.main, 0.04), border: '1px solid', borderColor: alpha(theme.palette.secondary.main, 0.1), mb: 3 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Estimated VO2 Max
                      </Typography>
                      <Typography variant="h3" color="secondary.main" sx={{ fontWeight: 800 }}>
                        {isEstimating ? '...' : estimateData?.estimated_vo2max || 'N/A'}
                      </Typography>
                      <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary', fontWeight: 500 }}>
                        Confidence: {estimateData?.confidence || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Estimated LTHR
                      </Typography>
                      <Typography variant="h3" color="info.main" sx={{ fontWeight: 800 }}>
                        {isEstimatingLTHR ? '...' : lthrEstimateData?.estimated_lthr || 'N/A'}
                      </Typography>
                      <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary', fontWeight: 500 }}>
                        Confidence: {lthrEstimateData?.confidence || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>

                <Alert severity="info" sx={{ borderRadius: 2.5, mb: 1, '& .MuiAlert-message': { fontWeight: 500 } }}>
                  Accuracy is improved with field tests. These provide a professional baseline.
                </Alert>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button
              onClick={() => {
                setShowVO2maxEstimateDialog(false);
                setShouldFetchEstimate(false);
              }}
              color="inherit"
              sx={{ fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                if (estimateData) setVo2max(estimateData.estimated_vo2max);
                if (lthrEstimateData) setLactateThresholdHeartRate(lthrEstimateData.estimated_lthr);
                setShowVO2maxEstimateDialog(false);
                setShouldFetchEstimate(false);
                setIsEditingMetrics(true);
                setIsEditingZones(true);
                setTimeout(() => {
                  handlePrefillHRZones();
                  handlePrefillPaceZones();
                }, 500);
              }}
              disabled={isEstimating || isEstimatingLTHR || (!estimateData && !lthrEstimateData)}
              sx={{ borderRadius: 2.5, px: 3, fontWeight: 700, textTransform: 'none' }}
            >
              Apply All & Edit
            </Button>
          </DialogActions>
        </Dialog>

        {/* LTHR Estimation Dialog */}
        <Dialog
          open={showLTHREstimateDialog}
          onClose={() => {
            setShowLTHREstimateDialog(false);
            setShouldFetchLTHREstimate(false);
          }}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
        >
          <DialogTitle sx={{ fontWeight: 800, letterSpacing: -0.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.1), color: 'info.main', display: 'flex' }}>
              <ZonesIcon sx={{ fontSize: 20 }} />
            </Box>
            LTHR Estimation
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 3, fontWeight: 500 }}>
              Estimate your Lactate Threshold Heart Rate based on your profile metrics.
            </DialogContentText>

            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Max HR"
                type="number"
                value={maxHr}
                onChange={(e) => {
                  setMaxHr(e.target.value === '' ? '' : Number(e.target.value));
                  setShouldFetchLTHREstimate(false);
                }}
                variant="outlined"
                InputProps={{ sx: { borderRadius: 3 }, endAdornment: <Typography variant="caption">BPM</Typography> }}
                helperText="Required for LTHR estimation"
              />
            </Box>

            {!shouldFetchLTHREstimate || !lthrEstimateData ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Button
                  variant="contained"
                  color="info"
                  size="large"
                  onClick={() => setShouldFetchLTHREstimate(true)}
                  disabled={!maxHr}
                  sx={{ borderRadius: 3, px: 4, fontWeight: 700, textTransform: 'none' }}
                >
                  Calculate LTHR
                </Button>
              </Box>
            ) : (
              <>
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, bgcolor: alpha(theme.palette.info.main, 0.04), border: '1px solid', borderColor: alpha(theme.palette.info.main, 0.1), mb: isEstimatingLTHR ? 0 : 3 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Estimated LTHR
                    </Typography>
                    <Typography variant="h3" color="info.main" sx={{ fontWeight: 800 }}>
                      {isEstimatingLTHR ? '...' : lthrEstimateData?.estimated_lthr || 'N/A'}
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary', fontWeight: 500 }}>
                      Confidence: {lthrEstimateData?.confidence || 'N/A'}
                    </Typography>
                  </Box>
                  {!isEstimatingLTHR && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', color: 'text.secondary' }}>
                        Method: {lthrEstimateData?.method || 'N/A'}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </>
            )}

            {isLTHREstimateError && (
              <Alert severity="error" sx={{ mt: 2 }}>Failed to calculate LTHR estimate.</Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button
              onClick={() => {
                setShowLTHREstimateDialog(false);
                setShouldFetchLTHREstimate(false);
              }}
              color="inherit"
              sx={{ fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="info"
              onClick={() => {
                if (lthrEstimateData) {
                  setLactateThresholdHeartRate(lthrEstimateData.estimated_lthr);
                }
                setShowLTHREstimateDialog(false);
                setShouldFetchLTHREstimate(false);
                setIsEditingZones(true);
                setTimeout(() => {
                  handlePrefillHRZones('lthr');
                }, 500);
              }}
              disabled={isEstimatingLTHR || !lthrEstimateData}
              sx={{ borderRadius: 2.5, px: 3, fontWeight: 700, textTransform: 'none' }}
            >
              Apply & Update Zones
            </Button>
          </DialogActions>
        </Dialog>


        {/* Strava Disconnect Dialog */}
        <Dialog
          open={showDisconnectDialog}
          onClose={() => setShowDisconnectDialogState(false)}
          PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
        >
          <DialogTitle sx={{ fontWeight: 800, letterSpacing: -0.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.1), color: 'warning.main', display: 'flex' }}>
              <LinkOffIcon sx={{ fontSize: 20 }} />
            </Box>
            Disconnect Strava?
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ fontWeight: 500 }}>
              Are you sure? You will stop receiving automatic activity syncs and historical data analysis for your runs.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button onClick={() => setShowDisconnectDialogState(false)} color="inherit" sx={{ fontWeight: 600 }}>Cancel</Button>
            <Button
              onClick={handleStravaDisconnectConfirm}
              color="warning"
              variant="contained"
              sx={{ borderRadius: 2.5, px: 4, fontWeight: 700, textTransform: 'none' }}
            >
              Disconnect Link
            </Button>
          </DialogActions>
        </Dialog>

        {/* Account Deletion Dialog */}
        <Dialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
        >
          <Box sx={{ p: 1, color: 'error.main', textAlign: 'center', mt: 3 }}>
            <Box sx={{ display: 'inline-flex', p: 1.5, borderRadius: '50%', bgcolor: alpha(theme.palette.error.main, 0.1), mb: 1 }}>
              <WarningIcon sx={{ fontSize: 32 }} />
            </Box>
          </Box>
          <DialogTitle sx={{ fontWeight: 800, textAlign: 'center', pt: 0, letterSpacing: -0.5 }}>Delete Account Forever?</DialogTitle>
          <DialogContent sx={{ textAlign: 'center' }}>
            <DialogContentText sx={{ mb: 3, fontWeight: 500, opacity: 0.9 }}>
              This action is permanent. All your history, workouts, and performance data will be deleted and cannot be recovered.
            </DialogContentText>
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 700, color: 'text.primary' }}>
              Type <strong>DELETE</strong> to confirm:
            </Typography>
            <TextField
              fullWidth
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder="DELETE"
              error={deleteConfirmation !== '' && deleteConfirmation !== 'DELETE'}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 3, fontWeight: 700, textAlign: 'center' },
                '& input': { textAlign: 'center' }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 4, pt: 1, flexDirection: 'column', gap: 1.5 }}>
            <Button
              onClick={handleAccountDeleteConfirm}
              color="error"
              variant="contained"
              fullWidth
              disabled={deleteConfirmation !== 'DELETE'}
              sx={{ borderRadius: 2.5, py: 1.25, fontWeight: 700, textTransform: 'none' }}
            >
              Delete My Data Forever
            </Button>
            <Button onClick={() => setShowDeleteDialog(false)} fullWidth color="inherit" sx={{ fontWeight: 600 }}>I changed my mind</Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            variant="filled"
            sx={{ width: '100%', borderRadius: 3, boxShadow: 6 }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default UserSettingsPage;
